// Web replacement for the Electron preload bridge (`window.pagepilot`).
//
// The original app exposed a context-isolated IPC object. Here we expose the
// *same* object shape so the ported React screens work unchanged, but each
// method is an HTTP call to the Cloudflare Worker. All 64 channels are
// dispatched through a single `POST /api/rpc` endpoint carrying the channel
// name + positional args — a thin, lightweight 1:1 mapping of the IPC surface.

// API origin. Empty = same-origin (dev uses the Vite proxy to the local Worker);
// in production builds this is the deployed Worker URL (VITE_API_BASE).
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

// Single-user app key (shared secret). Stored locally after the login prompt
// and sent on every API request; a 401 clears it and reloads to re-prompt.
const KEY_STORAGE = 'pagepilot.appKey';
export const getAppKey = (): string => localStorage.getItem(KEY_STORAGE) ?? '';
export const setAppKey = (k: string): void => localStorage.setItem(KEY_STORAGE, k);
export const clearAppKey = (): void => localStorage.removeItem(KEY_STORAGE);

/** Check a candidate key against the API (used by the login screen). */
export async function validateKey(key: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/rpc`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-app-key': key },
      body: JSON.stringify({ channel: 'engine:status', args: [] }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function invoke<T = unknown>(channel: string, ...args: unknown[]): Promise<T> {
  const res = await fetch(`${API_BASE}/api/rpc`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-app-key': getAppKey() },
    body: JSON.stringify({ channel, args }),
  });
  if (res.status === 401) {
    clearAppKey();
    location.reload();
    throw new Error('unauthorized');
  }
  const body = (await res.json()) as { ok: boolean; data?: T; error?: string };
  if (!res.ok || !body.ok) {
    throw new Error(body?.error ?? `request failed: ${channel} (${res.status})`);
  }
  return body.data as T;
}

// Asset URL helpers return server paths like "/api/assets/<id>"; make them
// absolute against the API origin so <img src> resolves cross-origin on Pages.
function absolutize(path: unknown): string | null {
  if (typeof path !== 'string' || !path) return null;
  if (!path.startsWith('/')) return path;
  // Asset URLs load via <img src> and can't set headers — carry the key in the
  // query string so they authenticate too.
  const key = getAppKey();
  return `${API_BASE}${path}${key ? `?k=${encodeURIComponent(key)}` : ''}`;
}

export const pagepilot = {
  engine: {
    status: () => invoke('engine:status'),
    activity: (limit?: number) => invoke('engine:activity', limit ?? 100),
    providerCheck: (kind: string) => invoke('engine:providerCheck', kind),
    pause: (pageId?: string) => invoke('engine:pause', pageId ?? null),
    resume: (pageId?: string) => invoke('engine:resume', pageId ?? null),
    jobRetry: (jobId: string) => invoke('engine:jobRetry', jobId),
    jobCancel: (jobId: string) => invoke('engine:jobCancel', jobId),
    ensureStarted: () => invoke('engine:ensureStarted'),
  },
  app: {
    quitCompletely: () => invoke('app:quitCompletely'),
  },
  setup: {
    get: () => invoke('setup:get'),
    testAndSaveDb: (input: Record<string, unknown>) => invoke('setup:testAndSaveDb', input),
  },
  pages: {
    list: () => invoke('pages:list'),
    create: (input: Record<string, unknown>) => invoke('pages:create', input),
    update: (id: string, patch: Record<string, unknown>) => invoke('pages:update', id, patch),
    archive: (id: string) => invoke('pages:archive', id),
    duplicate: (sourcePageId: string, input: Record<string, unknown>) => invoke('pages:duplicate', sourcePageId, input),
    getResearch: (pageId: string) => invoke('pages:getResearch', pageId),
    updateResearch: (pageId: string, config: Record<string, unknown>) => invoke('pages:updateResearch', pageId, config),
    listImageReferences: (pageId: string) => invoke('pages:listImageReferences', pageId),
    uploadImageReference: (input: Record<string, unknown>) => invoke('pages:uploadImageReference', input),
    removeImageReference: (pageId: string, assetId: string) => invoke('pages:removeImageReference', pageId, assetId),
    updateSchedule: (pageId: string, patch: Record<string, unknown>) => invoke('pages:updateSchedule', pageId, patch),
  },
  content: {
    list: (pageId?: string, contentType?: string) => invoke('content:list', pageId ?? '', contentType ?? ''),
    imageDataUrl: (contentItemId: string) => invoke('content:imageDataUrl', contentItemId).then(absolutize),
    assetDataUrl: (contentItemId: string, kind: string) => invoke('content:assetDataUrl', contentItemId, kind).then(absolutize),
    approve: (id: string) => invoke('content:approve', id),
    reject: (id: string, reason?: string) => invoke('content:reject', id, reason),
    remove: (id: string) => invoke('content:remove', id),
    generateNow: (pageId: string, contentType?: 'IMAGE_POST' | 'REEL') => invoke('content:generateNow', pageId, contentType),
    generateMonth: (pageId: string, contentType?: 'IMAGE_POST' | 'REEL', days?: number) => invoke('content:generateMonth', pageId, contentType, days),
    published: (pageId?: string) => invoke('content:published', pageId ?? ''),
  },
  inventory: {
    list: () => invoke('inventory:list'),
  },
  analytics: {
    summary: (pageId?: string) => invoke('analytics:summary', pageId ?? ''),
  },
  backup: {
    create: () => invoke('backup:create'),
    list: () => invoke('backup:list'),
    restoreConfig: (path: string) => invoke('backup:restoreConfig', path),
    restoreContent: (path: string) => invoke('backup:restoreContent', path),
    dir: () => invoke('backup:dir'),
  },
  assets: {
    list: (pageId?: string, kind?: string) => invoke('assets:list', pageId ?? '', kind ?? ''),
    dataUrl: (assetId: string) => invoke('assets:dataUrl', assetId).then(absolutize),
  },
  characters: {
    list: (pageId?: string) => invoke('characters:list', pageId ?? ''),
    create: (input: Record<string, unknown>) => invoke('characters:create', input),
    uploadMaster: (input: Record<string, unknown>) => invoke('characters:uploadMaster', input),
  },
  diagnostics: {
    get: () => invoke('diagnostics:get'),
  },
  calendar: {
    range: (fromIso: string, toIso: string, pageId?: string) => invoke('calendar:range', fromIso, toIso, pageId ?? ''),
    reschedule: (scheduleId: string, publishAtIso: string) => invoke('calendar:reschedule', scheduleId, publishAtIso),
    rescheduleLocal: (scheduleId: string, wallDateTime: string) => invoke('calendar:rescheduleLocal', scheduleId, wallDateTime),
  },
  fb: {
    connect: (input: Record<string, unknown>) => invoke('fb:connect', input),
  },
  prompts: {
    list: () => invoke('prompts:list'),
    get: (id: string) => invoke('prompts:get', id),
    import: (input: Record<string, unknown>) => invoke('prompts:import', input),
    saveVersion: (templateId: string, body: string, changeNote?: string) => invoke('prompts:saveVersion', templateId, body, changeNote),
    rollback: (templateId: string, versionId: string) => invoke('prompts:rollback', templateId, versionId),
    listProviderPreferences: () => invoke('prompts:listProviderPreferences'),
    setProviderPreference: (purpose: string, providerKind: string) => invoke('prompts:setProviderPreference', purpose, providerKind),
  },
  providers: {
    list: () => invoke('providers:list'),
    update: (kind: string, patch: Record<string, unknown>) => invoke('providers:update', kind, patch),
  },
  secrets: {
    set: (input: Record<string, unknown>) => invoke('secrets:set', input),
    list: () => invoke('secrets:list'),
  },
  notifications: {
    list: () => invoke('notifications:list'),
  },
  jobs: {
    list: (status?: string, pageId?: string) => invoke('jobs:list', status ?? 'ALL', pageId ?? ''),
  },
  settings: {
    get: (key: string) => invoke('settings:get', key),
    set: (key: string, value: unknown) => invoke('settings:set', key, value),
  },
};

export type PagePilotApi = typeof pagepilot;
